import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@veil/ui/components/alert";
import { Button } from "@veil/ui/components/button";
import { CheckCircle2, Copy, Loader2, PartyPopper, Play } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { RenderBody, useRender } from "@/pages/cluster/render-tab";
import type { ClusterResponse } from "@/pages/cluster/types";

type HealthState = "idle" | "checking" | "ok" | "fail";

/**
 * Step 5: review + launch. Runs Render & Reload (reusing the cluster render
 * state machine + SSE log), health-checks /health, then shows the connect URL.
 */
export function ReviewStep({
	onBack,
	onFinish,
}: {
	onBack: () => void;
	onFinish: () => void;
}) {
	const render = useRender({ active: true, canApply: true });
	const [health, setHealth] = useState<HealthState>("idle");
	const reduce = useReducedMotion();

	const clusterQuery = useQuery({
		queryKey: ["cluster"],
		queryFn: () => unwrap(api.api.cluster.get()) as Promise<ClusterResponse>,
		staleTime: 5_000,
	});

	const cluster = clusterQuery.data?.cluster ?? null;
	const publicHostname = cluster?.publicHostnames?.[0] ?? null;
	const connectUrl = publicHostname ? `wss://${publicHostname}/launch` : null;
	const managerHostname = cluster?.managerHostname ?? null;
	const releaseHostname = cluster?.releaseHostname ?? null;
	const uiColocated =
		!!managerHostname &&
		(cluster?.publicHostnames ?? []).includes(managerHostname);

	const succeeded =
		render.phase === "done" && render.applyStatus === "succeeded";

	// Health-check the LB once the reload succeeded.
	useEffect(() => {
		if (!succeeded) return;
		let cancelled = false;
		setHealth("checking");
		fetch("/health")
			.then((r) => {
				if (cancelled) return;
				setHealth(r.ok ? "ok" : "fail");
			})
			.catch(() => {
				if (!cancelled) setHealth("fail");
			});
		return () => {
			cancelled = true;
		};
	}, [succeeded]);

	const copyConnect = () => {
		if (!connectUrl) return;
		navigator.clipboard
			.writeText(connectUrl)
			.then(() => toast.success("Copied connect URL"))
			.catch(() => toast.error("Copy failed"));
	};

	if (succeeded) {
		return (
			<motion.div
				initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: reduce ? 0.12 : 0.28, ease: [0.25, 1, 0.5, 1] }}
			>
				<Alert className="mb-5">
					<motion.span
						initial={reduce ? false : { scale: 0.5, opacity: 0, rotate: -12 }}
						animate={{ scale: 1, opacity: 1, rotate: 0 }}
						transition={{
							delay: reduce ? 0 : 0.1,
							duration: 0.3,
							ease: "easeOut",
						}}
						className="flex"
					>
						<PartyPopper className="size-4" />
					</motion.span>
					<AlertTitle>Your cluster is live</AlertTitle>
					<AlertDescription>
						The configuration was rendered and OpenResty reloaded successfully.
					</AlertDescription>
				</Alert>

				<div className="rounded-lg border border-border/70 p-5">
					<div className="flex items-center gap-2 text-sm font-medium">
						<span
							className={
								health === "ok"
									? "flex items-center gap-1.5 text-emerald-600"
									: health === "fail"
										? "flex items-center gap-1.5 text-destructive"
										: "flex items-center gap-1.5 text-muted-foreground"
							}
						>
							{health === "checking" && (
								<Loader2 className="size-4 animate-spin" />
							)}
							{health === "ok" && <CheckCircle2 className="size-4" />}
							Load balancer health:{" "}
							{health === "ok"
								? "OK"
								: health === "fail"
									? "unreachable"
									: "checking…"}
						</span>
					</div>

					<div className="mt-4">
						<div className="text-xs font-medium text-muted-foreground">
							Connect your automation to
						</div>
						<div className="mt-1 flex items-center gap-2">
							<code className="flex-1 overflow-auto rounded-md bg-muted px-3 py-2 font-mono text-sm">
								{connectUrl ?? "wss://<public-hostname>/launch"}
							</code>
							{connectUrl && (
								<Button variant="outline" size="sm" onClick={copyConnect}>
									<Copy data-icon="inline-start" />
									Copy
								</Button>
							)}
						</div>
					</div>
				</div>

				<div className="mt-6 flex items-center justify-end">
					<Button onClick={onFinish} className="h-11">
						Go to dashboard
					</Button>
				</div>
			</motion.div>
		);
	}

	return (
		<div>
			<p className="mb-4 text-sm leading-relaxed text-muted-foreground">
				Review the configuration below, then render it to nginx and reload
				OpenResty.
			</p>

			<div className="mb-4 rounded-lg border border-border/60 p-4 text-sm">
				<div className="grid grid-cols-[8rem_1fr] gap-y-1.5">
					<span className="text-muted-foreground">Client /launch</span>
					<code className="font-mono">
						{publicHostname ? `wss://${publicHostname}/launch` : "—"}
					</code>
					<span className="text-muted-foreground">Manager UI</span>
					<code className="font-mono">
						{managerHostname
							? `https://${managerHostname}${uiColocated ? " (same host)" : ""}`
							: "raw host:port"}
					</code>
					<span className="text-muted-foreground">Release origin</span>
					<code className="font-mono">
						{releaseHostname ? `https://${releaseHostname}` : "internal only"}
					</code>
				</div>
			</div>

			<div className="max-h-72 overflow-auto rounded-lg border border-border/60 p-3">
				<RenderBody render={render} view="next" />
			</div>

			<div className="mt-6 flex items-center justify-between gap-2">
				<Button
					variant="ghost"
					onClick={onBack}
					disabled={render.phase === "applying"}
				>
					Back
				</Button>
				{render.phase === "done" && render.applyStatus === "failed" ? (
					<Button onClick={render.retry}>Retry</Button>
				) : (
					<Button
						onClick={render.startApply}
						disabled={render.phase === "applying" || render.previewLoading}
					>
						{render.phase === "applying" ? (
							<Loader2 data-icon="inline-start" className="animate-spin" />
						) : (
							<Play data-icon="inline-start" />
						)}
						{render.phase === "applying" ? "Rendering…" : "Render & Reload"}
					</Button>
				)}
			</div>
		</div>
	);
}
